import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useQuery, useReactiveVar } from '@apollo/client'
import { useErrorHandler } from 'react-error-boundary'
import { upperFirstOnly } from 'utils/format'
import { fuelGradeTitlesToIds } from 'utils/helpers'
import { createDateRangeFromNow, dateToString } from 'utils/dateTime'
import { withEdgeErrorBoundary } from 'components/common/with-edge-error-boundary'
import { WidgetFallback } from 'components/common/widget-fallback'
import { DataTable } from 'components/common/performance-report/components/data-table'
import { ExportButton } from '../export-button'
import { dashboardNameForTab } from 'config/posthog/events'
import { useFormatMetricValue } from 'components/common/hooks/useFormatMetricValue'
import { MetricTypes, DisplayFuelGrades } from 'utils/constants'
import moment from 'moment'
import { useFuelGrades } from 'components/common/hooks/useFuelGrades'
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone'
import { useStationGroups } from 'components/analytics/context/StationGroupsContext'
import {
  TANK_FUEL_LEVELS_AND_DELIVERIES_QUERY,
  TANK_FUEL_LEVELS_AND_DELIVERIES_BY_FUEL_GRADE_QUERY,
} from '../../StockAndDeliveriesQueries'
import { StockAndDeliveriesChart } from '../stock-and-deliveries-chart'
import {
  stationsVar,
  fuelGradesVar,
  currentPeriodDateVar,
  splitByVar,
} from 'config/apollo/analytics-report/variables'
import classNames from 'classnames/bind'
import styles from './StockAndDeliveriesReport.module.scss'
import Spinner from 'react-bootstrap/Spinner'

const cx = classNames.bind(styles)

// Sums one fact across every tank for a single day, keeping the backend's
// string-or-null contract: a day is null only when every tank is null.
const sumSeriesAt = (tanks, key, index) => {
  const values = tanks
    .map((tank) => parseFloat(tank[key]?.[index]))
    .filter((value) => !Number.isNaN(value))

  return values.length ? String(values.reduce((total, value) => total + value, 0)) : null
}

// Collapses each fuel grade's tanks into a single series named after the
// grade, so dividing by fuel grade shows one bar per grade per day. The output
// keeps the chart's data shape (a grade series stands in for a tank).
const buildFuelGradeSeries = (data, fuelGradesById) => {
  if (!data?.dates?.length || !data?.grades?.length) {
    return { dates: data?.dates ?? [], tanks: [] }
  }

  return {
    dates: data.dates,
    tanks: data.grades.map((grade) => ({
      tankName: fuelGradesById[grade.fuelGradeId]?.title ?? `${grade.fuelGradeId}`,
      colour: fuelGradesById[grade.fuelGradeId]?.primary ?? null,
      tankFuelLevel: data.dates.map((_, index) => sumSeriesAt(grade.tanks, 'tankFuelLevel', index)),
      deliveredFuelVolume: data.dates.map((_, index) => sumSeriesAt(grade.tanks, 'deliveredFuelVolume', index)),
      fuelValueInStock: data.dates.map((_, index) => sumSeriesAt(grade.tanks, 'fuelValueInStock', index)),
      fuelVolumeSold: data.dates.map((_, index) => sumSeriesAt(grade.tanks, 'fuelVolumeSold', index)),
    })),
  }
}

const TABLE_METRICS = [
  { key: 'tankFuelLevel', labelKey: 'widgets.analytics.fuelInTankAtEndOfDay', metricType: MetricTypes.VOLUME },
  { key: 'fuelValueInStock', labelKey: 'widgets.analytics.valueOfFuelAtEndOfDay', metricType: MetricTypes.PROFIT },
  { key: 'fuelVolumeSold', labelKey: 'widgets.analytics.volumeSoldDuringTheDay', metricType: MetricTypes.VOLUME },
  { key: 'deliveredFuelVolume', labelKey: 'widgets.analytics.deliveredDuringTheDay', metricType: MetricTypes.VOLUME },
]

// One DataTable column per series-and-metric pair, keeping the chart's series
// order: every metric for each tank (or grade) in turn. Cell values move from
// the backend's string-or-null contract to the number-or-null shape the
// table formats.
const buildTableColumns = (data) => {
  if (!data?.dates?.length || !data?.tanks?.length) {
    return []
  }

  return data.tanks.flatMap((tank) =>
    TABLE_METRICS.map((fact) => ({
      tankName: tank.tankName,
      colour: tank.colour ?? null,
      labelKey: fact.labelKey,
      metricType: fact.metricType,
      cells: data.dates.map((date, index) => {
        const parsed = parseFloat(tank[fact.key]?.[index])

        return { date, value: Number.isNaN(parsed) ? null : parsed }
      }),
    }))
  )
}

// The CSV spells each column's unit out in the header: the locale's volume
// word (litres/gallons) for the fuel facts, its currency symbol for the
// stock value.
const EXPORT_UNIT_KEYS = {
  [MetricTypes.VOLUME]: 'widgets.analytics.volumeUnit',
  [MetricTypes.PROFIT]: 'currency.short',
}

// One CSV row per date, one column per series fact, formatted like the table
// so the download mirrors what the dashboard shows.
const buildExportRows = (seriesData, t, formatMetricValue) => {
  const columns = buildTableColumns(seriesData)

  if (!columns.length) {
    return []
  }

  return seriesData.dates.map((date, index) => {
    const row = { Date: moment.utc(date).format('Do MMM YYYY') }

    columns.forEach((column) => {
      const { value } = column.cells[index]
      const unit = t(EXPORT_UNIT_KEYS[column.metricType])
      row[`${column.tankName} ${t(column.labelKey)} (${unit})`] =
        value === null ? '' : formatMetricValue(column.metricType, value, false)
    })

    return row
  })
}

const StockAndDeliveriesReport = ({ activeTab, mainData }) => {
  const { t } = useTranslation(['translations'])
  const { formatMetricValue } = useFormatMetricValue()
  const { companyTimeZone } = useCompanyTimeZone()
  const isEdgeDataPro = mainData?.me.company.edgeDataPro
  const { groups } = useStationGroups()

  const stationsFilter = useReactiveVar(stationsVar)
  const fuelGradesFilter = useReactiveVar(fuelGradesVar)
  const currentPeriodDateFilter = useReactiveVar(currentPeriodDateVar)
  const splitByFilter = useReactiveVar(splitByVar)
  const { fuelGrades: allFuelGrades, fuelGradesLoading } = useFuelGrades(
    stationsFilter[activeTab],
    null,
    isEdgeDataPro,
    groups
  )

  const stationId = stationsFilter[activeTab]?.[0]
  // The filters hold fuel grade titles; the API wants a single numeric id.
  const fuelGradeId = parseInt(
    fuelGradeTitlesToIds(fuelGradesFilter[activeTab] || [], allFuelGrades)[0],
    10
  )

  const dividedByFuelGrade = splitByFilter[activeTab]?.[0] === DisplayFuelGrades
  // Dividing by fuel grade shows every grade at the station, ignoring the
  // single-grade filter (the dropdown is disabled in that mode).
  const allFuelGradeIds = (allFuelGrades ?? []).map((fuelGrade) => parseInt(fuelGrade.id, 10))
  const fuelGradesById = Object.fromEntries(
    (allFuelGrades ?? []).map((fuelGrade) => [parseInt(fuelGrade.id, 10), fuelGrade])
  )

  // Mirrors the period dropdown's last-2-weeks default for this tab.
  const defaultRange = createDateRangeFromNow(-14, -1, companyTimeZone)
  const currentPeriodStartDate = dateToString(
    companyTimeZone,
    currentPeriodDateFilter[activeTab]?.from || defaultRange.from
  )
  const currentPeriodEndDate = dateToString(
    companyTimeZone,
    currentPeriodDateFilter[activeTab]?.to || defaultRange.to
  )

  // Each query is skipped until the upstream filter data (stations, fuel
  // grades) it depends on has resolved.
  const tanksQueryUnready =
    !stationId ||
    !fuelGradesFilter[activeTab]?.length ||
    fuelGradesLoading ||
    Number.isNaN(fuelGradeId)
  const byFuelGradeQueryUnready =
    !stationId ||
    fuelGradesLoading ||
    !allFuelGradeIds.length

  // Both datasets are fetched regardless of the divide toggle: the active one
  // drives the chart and table, the other feeds its export button.
  const { data, loading, error } = useQuery(TANK_FUEL_LEVELS_AND_DELIVERIES_QUERY, {
    variables: {
      stationId,
      fuelGradeId,
      currentPeriodStartDate,
      currentPeriodEndDate,
    },
    skip: tanksQueryUnready,
  })

  const {
    data: byFuelGradeData,
    loading: byFuelGradeLoading,
    error: byFuelGradeError,
  } = useQuery(TANK_FUEL_LEVELS_AND_DELIVERIES_BY_FUEL_GRADE_QUERY, {
    variables: {
      stationId,
      fuelGradeIds: allFuelGradeIds,
      currentPeriodStartDate,
      currentPeriodEndDate,
    },
    skip: byFuelGradeQueryUnready,
  })

  // Only the on-display dataset's failure blows the widget; a broken
  // background dataset just leaves its export button out.
  useErrorHandler(dividedByFuelGrade ? byFuelGradeError : error)

  const tanksSeries = data?.tankFuelLevelsAndDeliveries
  const gradeSeries = buildFuelGradeSeries(
    byFuelGradeData?.tankFuelLevelsAndDeliveriesByFuelGrade,
    fuelGradesById
  )

  const rawData = dividedByFuelGrade
    ? byFuelGradeData?.tankFuelLevelsAndDeliveriesByFuelGrade
    : tanksSeries

  // A skipped query reports loading: false with no data, so while the
  // on-display query's inputs are still unready, show the spinner rather
  // than flashing the empty state.
  const queryAwaitingUpstream =
    !rawData && (dividedByFuelGrade ? byFuelGradeQueryUnready : tanksQueryUnready)

  const activeLoading = dividedByFuelGrade ? byFuelGradeLoading : loading

  if (activeLoading || queryAwaitingUpstream) {
    return (
      <div className={cx('pro-widget-spinner')}>
        <Spinner animation="grow" variant="primary" />
      </div>
    )
  }

  // Every tank in single-grade mode holds the selected grade, so each series
  // wears that grade's brand colour.
  const selectedGradeColour = fuelGradesById[fuelGradeId]?.primary ?? null
  // The tanks export always covers a single grade — even when the chart is
  // divided by fuel grade — so the button and filename carry the grade's name.
  const selectedGradeTitle = fuelGradesById[fuelGradeId]?.title
  const selectedGradeSlug = selectedGradeTitle
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  const tanksSeriesWithColours = tanksSeries && {
    ...tanksSeries,
    tanks: tanksSeries.tanks.map((tank) => ({ ...tank, colour: selectedGradeColour })),
  }
  const chartData = dividedByFuelGrade ? gradeSeries : tanksSeriesWithColours

  if (!chartData?.dates?.length || !chartData?.tanks?.length) {
    return (
      <div className={cx('empty-state')}>
        <div className={cx('empty-state-content')}>
          <h2>{upperFirstOnly(t('widgets.analytics.noDataTitle'))}</h2>
          <p>{t('widgets.analytics.noDataMessage')}</p>
        </div>
      </div>
    )
  }

  // The legend names every grade on show: each grade series in divided mode,
  // or just the single selected grade when the bars are tanks.
  const legendGrades = dividedByFuelGrade
    ? chartData.tanks.map(({ tankName, colour }) => ({ title: tankName, colour }))
    : [{ title: fuelGradesById[fuelGradeId]?.title, colour: selectedGradeColour }].filter(
        (grade) => grade.title
      )

  const tableColumns = buildTableColumns(chartData)
  // The date column stays pinned while the per-tank columns scroll under it.
  const stickyColumns = [{ metric: 'Date', width: 170 }]

  const hasTanksExport = Boolean(tanksSeries?.dates?.length && tanksSeries?.tanks?.length)
  const hasGradesExport = Boolean(gradeSeries.dates.length && gradeSeries.tanks.length)

  return (
    <>
      <StockAndDeliveriesChart
        data={chartData}
        grades={legendGrades}
        dividedByFuelGrade={dividedByFuelGrade}
      />
      <div className={cx('export-buttons')}>
        {hasTanksExport && (
          <ExportButton
            data={() => buildExportRows(tanksSeriesWithColours, t, formatMetricValue)}
            filename={`stock_and_deliveries_tanks_${
              selectedGradeSlug ? `${selectedGradeSlug}_` : ''
            }${currentPeriodStartDate}-${currentPeriodEndDate}`}
            buttonName={
              selectedGradeTitle
                ? t('widgets.analytics.exportTanksForGrade', { grade: selectedGradeTitle })
                : t('widgets.analytics.exportTanks')
            }
            padding="0"
            margin="0"
            reportName={dashboardNameForTab(activeTab)}
            period={currentPeriodDateFilter[activeTab]}
            exportType="tanks"
          />
        )}
        {hasGradesExport && (
          <ExportButton
            data={() => buildExportRows(gradeSeries, t, formatMetricValue)}
            filename={`stock_and_deliveries_fuel_grades_${currentPeriodStartDate}-${currentPeriodEndDate}`}
            buttonName={t('widgets.analytics.exportFuelGrades')}
            padding="0"
            margin="0"
            reportName={dashboardNameForTab(activeTab)}
            period={currentPeriodDateFilter[activeTab]}
            exportType="fuel_grades"
          />
        )}
      </div>
      <div className={cx('table-container')} data-testid="stock-and-deliveries-table">
        <DataTable
          data={tableColumns.map((column) => column.cells)}
          metrics={tableColumns.map((column) => `${column.tankName} ${t(column.labelKey)}`)}
          metricTypes={tableColumns.map((column) => column.metricType)}
          headerColours={tableColumns.map((column) => column.colour)}
          metricType={MetricTypes.VOLUME}
          previousColumn={false}
          analytics={true}
          hideFooter={true}
          stickyColumns={stickyColumns}
          enableColors={false}
        />
      </div>
    </>
  )
}

StockAndDeliveriesReport.propTypes = {
  activeTab: PropTypes.string.isRequired,
  mainData: PropTypes.shape({
    me: PropTypes.shape({
      company: PropTypes.shape({
        edgeDataPro: PropTypes.bool,
      }),
    }),
  }),
}

const FallbackComponent = () => <WidgetFallback />

const StockAndDeliveriesReportWithErrorBoundary = withEdgeErrorBoundary(
  StockAndDeliveriesReport,
  FallbackComponent,
  'StockAndDeliveriesReport'
)

export {
  StockAndDeliveriesReportWithErrorBoundary as StockAndDeliveriesReport,
  buildFuelGradeSeries,
  buildTableColumns,
  buildExportRows,
}
